import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target, RefreshCw, TrendingUp, Users, MapPin } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MatchingPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [matchRes, dealsRes] = await Promise.all([
        axios.get(`${API}/matching/deals`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deals`, { headers: getAuthHeaders() })
      ]);
      setMatches(matchRes.data);
      setDeals(dealsRes.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMatches = async () => {
    if (!selectedDeal) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/matches/generate/${selectedDeal}`, {}, { headers: getAuthHeaders() });
      toast.success(`Generated ${res.data.matches_created} new matches`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate matches');
    } finally {
      setGenerating(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 9) return 'text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400';
    if (score >= 7.5) return 'text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-400';
    if (score >= 6) return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-950 dark:text-yellow-400';
    return 'text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading matches...</div></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="matching-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Investor Matching</h1>
          <p className="text-muted-foreground">AI-powered matching of deals with investors and buyers</p>
        </div>
      </div>

      <Card data-testid="generate-matches-card">
        <CardHeader>
          <CardTitle className="text-base">Generate New Matches</CardTitle>
          <CardDescription>Select a deal to find matching investors and buyers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Select value={selectedDeal} onValueChange={setSelectedDeal}>
                <SelectTrigger data-testid="deal-selector">
                  <SelectValue placeholder="Select a deal..." />
                </SelectTrigger>
                <SelectContent>
                  {deals.map(deal => (
                    <SelectItem key={deal.id} value={deal.id}>{deal.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generateMatches} disabled={!selectedDeal || generating} data-testid="generate-btn">
              <RefreshCw size={16} className={`mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Generate Matches'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Matches</p>
                <p className="text-2xl font-bold font-mono">{matches.length}</p>
              </div>
              <Target className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Match Score</p>
                <p className="text-2xl font-bold font-mono">
                  {matches.length > 0 ? (matches.reduce((a, m) => a + m.match_score, 0) / matches.length).toFixed(1) : '0.0'}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unique Investors</p>
                <p className="text-2xl font-bold font-mono">
                  {new Set(matches.map(m => m.matched_user_id)).size}
                </p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {matches.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No matches yet. Select a deal and generate matches.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {matches.map((match) => (
            <Card key={match.id} className="hover:shadow-md transition-shadow" data-testid={`match-${match.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-sm">{match.deal?.name || 'Unknown Deal'}</h3>
                      <Badge variant="outline" className="text-xs">{match.deal?.sector}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><MapPin size={12} />{match.deal?.country}</span>
                      <span className="font-mono">{formatCurrency(match.deal?.capital_required || 0)}</span>
                    </div>
                  </div>
                  <div className="text-center px-4">
                    <div className="text-xs text-muted-foreground mb-1">Matched To</div>
                    <p className="font-semibold text-sm">{match.matched_user?.full_name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground capitalize">{match.matched_user?.role} - {match.matched_user?.organization}</p>
                  </div>
                  <div className="text-right pl-4">
                    <div className={`inline-flex items-center px-3 py-1.5 rounded font-mono text-lg font-bold ${getScoreColor(match.match_score)}`}>
                      {match.match_score.toFixed(1)}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1 justify-end">
                      {match.match_reasons?.map((r, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px]">{r}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
